import { getEffectiveLocationIdForDisplay } from '../location-hierarchy/locationScopeUtils';
import { getParentTaskIds } from '../features/dashboard/logic/prepareDashboardData';
import type { TaskSnapshot } from '../stores/types';
import { normalizeTaskType } from '../utils/utils';

export type BulkUnknownTaskType = 'construction_activity' | 'location';

export type ClassifiedUnknownTaskTypeUpdate = {
  taskId: string;
  taskType: BulkUnknownTaskType;
  /** Set only when taskType is location and a resolvable id exists. */
  locationId?: string;
};

export type ClassifyUnknownTaskTypesResult = {
  updates: ClassifiedUnknownTaskTypeUpdate[];
  constructionActivityCount: number;
  locationCount: number;
  skippedSummaryNoLocationCount: number;
  /** Tasks whose type was unknown/empty before classification. */
  unknownCount: number;
};

/** Empty or explicit unknown — never overwrite phase / preparatory / etc. */
export function isUnknownTaskType(taskType: string | null | undefined): boolean {
  const normalized = normalizeTaskType(taskType);
  return normalized == null || normalized === 'unknown';
}

/**
 * Classify unknown tasks for bulk type fix:
 * - leaf (no children) → construction_activity
 * - summary (has children) → location only when locationId is own or inherited
 * - summary without resolvable locationId → skipped
 */
export function classifyUnknownTaskTypes(
  tasks: TaskSnapshot[],
  getTask: (id: string) =>
    | {
        parentTaskId?: string | null;
        taskType?: string | null;
        locationId?: string | null;
      }
    | undefined = (id) => tasks.find((t) => String(t.id) === String(id)),
): ClassifyUnknownTaskTypesResult {
  const parentIds = getParentTaskIds(tasks);
  const updates: ClassifiedUnknownTaskTypeUpdate[] = [];
  let constructionActivityCount = 0;
  let locationCount = 0;
  let skippedSummaryNoLocationCount = 0;
  let unknownCount = 0;

  for (const task of tasks) {
    if (!isUnknownTaskType(task.taskType)) continue;
    unknownCount += 1;
    const taskId = String(task.id);
    const isSummary = parentIds.has(taskId);

    if (!isSummary) {
      updates.push({ taskId, taskType: 'construction_activity' });
      constructionActivityCount += 1;
      continue;
    }

    const ownId =
      task.locationId != null && String(task.locationId).trim() !== ''
        ? String(task.locationId).trim()
        : null;
    const locationId = ownId ?? getEffectiveLocationIdForDisplay(getTask, taskId);
    if (!locationId) {
      skippedSummaryNoLocationCount += 1;
      continue;
    }

    updates.push({ taskId, taskType: 'location', locationId });
    locationCount += 1;
  }

  return {
    updates,
    constructionActivityCount,
    locationCount,
    skippedSummaryNoLocationCount,
    unknownCount,
  };
}

/** Chunk size for updateTaskMeta — backend applies one UPDATE per item. */
export const BULK_TASK_META_CHUNK_SIZE = 250;
